import { useMediaQuery } from "@vueuse/core";
import { type ComputedRef, type MaybeRef, type Ref, computed, reactive, unref } from "vue";

import type { Permission } from "@/services/PermissionsService";

export type MenuItem = {
    label: string;
    icon?: string;
    to?: string;
    separator?: boolean;
    items?: MenuItem[];
    permission?: Permission | Permission[];
};

type LayoutConfig = {
    menuMode: "drawer";
};

type LayoutState = {
    staticMenuDesktopInactive: boolean;
    overlayMenuActive: boolean;
    configSidebarVisible: boolean;
    menuHoverActive: boolean;
    topbarMenuActive: boolean;
    sidebarActive: boolean;
    anchored: boolean;
    activeMenuItem: string | null;
    overlaySubmenuActive: boolean;
    mobileMenuActive: boolean;
};

type UseLayout = {
    layoutConfig: LayoutConfig;
    layoutState: LayoutState;
    isSidebarActive: ComputedRef<boolean>;
    isMobile: Ref<boolean>;
    toggleMobileMenu: () => void;
    closeMobileMenu: () => void;
    setActiveMenuItem: (item: MaybeRef<string | null>) => void;
};

const layoutConfig = reactive<LayoutConfig>({
    menuMode: "drawer",
});

const layoutState = reactive<LayoutState>({
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    configSidebarVisible: false,
    menuHoverActive: false,
    topbarMenuActive: false,
    sidebarActive: localStorage.getItem("sidebarAnchored") === "true",
    anchored: localStorage.getItem("sidebarAnchored") === "true",
    activeMenuItem: null,
    overlaySubmenuActive: false,
    mobileMenuActive: false,
});

const isMobile = useMediaQuery("(max-width: 767px)");

export function useLayout(): UseLayout {
    function setActiveMenuItem(item: MaybeRef<string | null>): void {
        layoutState.activeMenuItem = unref(item);
    }

    const isSidebarActive = computed(() => layoutState.overlayMenuActive || layoutState.overlaySubmenuActive);

    function toggleMobileMenu(): void {
        layoutState.mobileMenuActive = !layoutState.mobileMenuActive;
    }

    function closeMobileMenu(): void {
        layoutState.mobileMenuActive = false;
    }

    return {
        layoutConfig,
        layoutState,
        isSidebarActive,
        isMobile,
        toggleMobileMenu,
        closeMobileMenu,
        setActiveMenuItem,
    };
}
